import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { apiGet } from "../../shared/apiClient";
import { deleteProductById, fetchProductsListView } from "../../shared/productsApi";
import { resolveMediaUrl } from "../../shared/mediaUrl";
import { IconEye, IconPlus } from "../../shared/toolbarIcons";
import { RecordViewDrawer } from "../../shared/RecordDrawer";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

function trunc(s, max = 28) {
  if (s == null || s === "") return "—";
  const t = String(s);
  return t.length <= max ? t : `${t.slice(0, max)}…`;
}

const dataGridSx = {
  border: "none",
  fontFamily: "inherit",
  fontSize: "0.9rem",
  height: "100%",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#f2fcf5",
    borderBottom: "1px solid #e8ecea",
    fontWeight: 600
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid #e8ecea",
    backgroundColor: "#fbfffc"
  },
  "& .MuiDataGrid-row:nth-of-type(even):not(.MuiDataGrid-row--editing):not(:hover)": {
    backgroundColor: "#fcfffd"
  },
  "& .MuiDataGrid-cell": {
    borderBottomColor: "#e8ecea"
  }
};

export function ProductsList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewProduct, setViewProduct] = useState(null);
  const [viewBrandName, setViewBrandName] = useState("—");
  const [viewCategoryName, setViewCategoryName] = useState("—");

  const brandById = useMemo(() => {
    const m = new Map();
    for (const b of brands) m.set(b.id, b);
    return m;
  }, [brands]);

  const categoryById = useMemo(() => {
    const m = new Map();
    for (const c of categories) m.set(c.id, c);
    return m;
  }, [categories]);

  const rows = useMemo(() => {
    return items.map((x) => {
      const brand = brandById.get(x.brandId);
      const cat = categoryById.get(x.categoryId);
      return {
        id: x.id,
        product: x,
        brandName: brand?.name ?? "—",
        categoryName: cat?.name ?? "—",
        summary: trunc(x.shortDescription, 32),
        summaryFull: x.shortDescription || ""
      };
    });
  }, [items, brandById, categoryById]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await fetchProductsListView();
    if (!result.ok) {
      setError(result.error ?? "Failed to load data");
      setItems([]);
      setBrands([]);
      setCategories([]);
    } else {
      setItems(result.products);
      setBrands(result.brands);
      setCategories(result.categories);
    }
    setLoading(false);
  }, []);

  const handleDeleteClick = useCallback(
    async (id) => {
      if (!(await window.confirmDialog("Delete this product?"))) return;
      const res = await deleteProductById(id);
      if (!res.ok) setError(res.error ?? "Failed to delete product");
      else await load();
    },
    [load]
  );

  const handleViewClick = useCallback(
    async (row) => {
      const res = await apiGet(`/api/Products/${row.id}`);
      if (!res.ok) {
        setError(res.error ?? "Failed to load product details");
        return;
      }

      setViewProduct(res.data);
      setViewBrandName(row.brandName);
      setViewCategoryName(row.categoryName);
    },
    []
  );

  const columns = useMemo(
    () => [
      {
        field: "image",
        headerName: "Image",
        width: 90,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const image = resolveMediaUrl(params.row.product.image);
          const name = params.row.product.name || "Product";
          if (!image) return <span>—</span>;
          return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
              <img
                src={image}
                alt={name}
                style={{
                  width: 42,
                  height: 42,
                  objectFit: "cover",
                  borderRadius: 10,
                  border: "1px solid #d9f5e0",
                  background: "#f8fffb"
                }}
              />
            </div>
          );
        }
      },
      {
        field: "name",
        headerName: "Name",
        flex: 0.9,
        minWidth: 110,
        valueGetter: (_v, row) => row.product.name ?? "—"
      },
      {
        field: "slug",
        headerName: "Slug",
        flex: 0.75,
        minWidth: 90,
        valueGetter: (_v, row) => row.product.slug ?? "—"
      },
      {
        field: "brandName",
        headerName: "Brand",
        flex: 0.75,
        minWidth: 90,
        valueGetter: (_v, row) => row.brandName
      },
      {
        field: "categoryName",
        headerName: "Category",
        flex: 0.8,
        minWidth: 95,
        valueGetter: (_v, row) => row.categoryName
      },
      {
        field: "vendorId",
        headerName: "Vendor",
        width: 80,
        valueGetter: (_v, row) => row.product.vendorId ?? "—"
      },
      {
        field: "code",
        headerName: "Code",
        width: 88,
        valueGetter: (_v, row) => row.product.code ?? "—"
      },
      {
        field: "basePrice",
        headerName: "Base",
        width: 72,
        valueGetter: (_v, row) => row.product.basePrice ?? 0
      },
      {
        field: "salePrice",
        headerName: "Sale",
        width: 72,
        valueGetter: (_v, row) => row.product.salePrice ?? 0
      },
      {
        field: "minQuantity",
        headerName: "Min qty",
        width: 76,
        valueGetter: (_v, row) => row.product.minQuantity ?? "—"
      },
      {
        field: "summary",
        headerName: "Summary",
        flex: 1,
        minWidth: 100,
        valueGetter: (_v, row) => row.summary
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 170,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const row = params.row;
          const x = row.product;
          return (
            <div className="table-actions">
              <button className="icon-button" type="button" title="View" onClick={() => void handleViewClick(row)}>
                <IconEye />
              </button>
              <button className="icon-button" type="button" title="Edit" onClick={() => navigate(`/products?edit=${x.id}`)}>
                ✏️
              </button>
              <button className="icon-button danger" type="button" title="Delete" onClick={() => void handleDeleteClick(x.id)}>
                🗑️
              </button>
            </div>
          );
        }
      }
    ],
    [navigate, handleDeleteClick, handleViewClick]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const total = rows.length;

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Products</h2>
          <p>{loading ? "Loading products..." : `Total: ${total}`}</p>
        </div>
        <div className="card-header-actions">
          <Link to="/products" className="icon-button icon-button--toolbar" title="Add product" aria-label="Add product">
            <IconPlus />
          </Link>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={dataGridSx}
          />
        </div>
      </div>
      <RecordViewDrawer
        open={Boolean(viewProduct)}
        onClose={() => {
          setViewProduct(null);
          setViewBrandName("—");
          setViewCategoryName("—");
        }}
        label="Product details"
        title={viewProduct?.name ?? "—"}
        subtitle={viewProduct?.slug}
        ariaLabel="Product details"
        images={
          viewProduct?.image
            ? [{ label: "Image", src: resolveMediaUrl(viewProduct.image), linkText: "Open image" }]
            : []
        }
        sections={
          viewProduct
            ? [
                {
                  title: "Basic Details",
                  columns: 2,
                  fields: [
                    ["Name", viewProduct.name],
                    ["Slug", viewProduct.slug],
                    ["Code", viewProduct.code],
                    ["Barcode", viewProduct.barcode],
                    ["Brand", viewBrandName],
                    ["Category", viewCategoryName],
                    ["Vendor Id", viewProduct.vendorId],
                    ["Currency Id", viewProduct.currencyId]
                  ]
                },
                {
                  title: "Pricing",
                  columns: 2,
                  fields: [
                    ["Base Price", viewProduct.basePrice],
                    ["Sale Price", viewProduct.salePrice],
                    ["Min Quantity", viewProduct.minQuantity],
                    ["Weight", viewProduct.weight],
                    ["Length", viewProduct.length],
                    ["Width", viewProduct.width],
                    ["Height", viewProduct.height]
                  ]
                },
                {
                  title: "Status",
                  columns: 2,
                  fields: [
                    ["Active", viewProduct.isActive],
                    ["Featured", viewProduct.isFeatured],
                    ["Digital", viewProduct.isDigital],
                    ["Avg Rating", viewProduct.avgRating],
                    ["Rating Count", viewProduct.ratingCount]
                  ]
                },
                {
                  title: "Descriptions",
                  fields: [
                    ["Short Description", viewProduct.shortDescription, true],
                    ["Long Description", viewProduct.longDescription, true],
                    ["Meta Title", viewProduct.metaTitile ?? viewProduct.metaTitle],
                    ["Meta Description", viewProduct.metaDescription, true]
                  ]
                }
              ]
            : []
        }
      />
    </div>
  );
}
